// src/components/ui/DateField.tsx
// Modern date picker (date-only) using Popover + Calendar.
// Drop-in replacement for native <input type="date">.
import * as React from 'react';
import { Calendar as CalIcon, X as XIcon } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Calendar } from './calendar';
import { cn } from '../../lib/utils';

interface DateFieldProps {
  value?: string | null;            // "yyyy-MM-dd"
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md';
}

export const DateField: React.FC<DateFieldProps> = ({
  value,
  onChange,
  placeholder = 'Select date',
  disabled,
  className,
  size = 'md',
}) => {
  const [open, setOpen] = React.useState(false);

  const parsed = React.useMemo(() => {
    if (!value) return undefined;
    try {
      const d = parseISO(value);
      return isValid(d) ? d : undefined;
    } catch {
      return undefined;
    }
  }, [value]);

  const display = parsed ? format(parsed, 'MMM d, yyyy') : '';

  const handleSelect = (d?: Date) => {
    if (!d) {
      onChange('');
      setOpen(false);
      return;
    }
    onChange(format(d, 'yyyy-MM-dd'));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'w-full inline-flex items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white text-left transition-colors',
            'hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            'disabled:opacity-60 disabled:cursor-not-allowed',
            size === 'sm' ? 'px-3 py-1.5 text-sm' : 'px-3 py-2.5 text-sm',
            className
          )}
        >
          <span className="flex items-center gap-2 min-w-0 flex-1">
            <CalIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <span className={cn('truncate gt-numerals', !display && 'text-gray-400')}>
              {display || placeholder}
            </span>
          </span>
          {parsed && !disabled && (
            <span
              role="button"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              className="text-gray-400 hover:text-gray-600 p-0.5 rounded hover:bg-gray-100"
              aria-label="Clear date"
            >
              <XIcon className="w-3.5 h-3.5" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-auto"
        sideOffset={6}
      >
        <Calendar
          mode="single"
          selected={parsed}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

export default DateField;
